import { FormGroup } from '@angular/forms';
import { _ } from '@ngx-translate/core';
import { ZodError, ZodIssue } from 'zod';

function issueToTranslationKey(issue: ZodIssue): string {
  switch (issue.code) {
    case 'too_small':
      return issue.origin === 'string' && issue.minimum === 1
        ? 'validation.required'
        : 'validation.required';
    case 'too_big':
      return 'validation.maxLength';
    case 'invalid_value':
      return 'validation.invalidOption';
    case 'invalid_format':
      return 'validation.invalidDate';
    case 'custom':
      // Refinements carry their translation key as the message.
      return issue.message || 'validation.invalid';
    default:
      return 'validation.invalid';
  }
}

/**
 * Walks the full `issue.path` and resolves it to an Angular form-control path
 * suitable for `form.get(...)`. Numeric segments stay as numbers (Angular's
 * FormArray accepts numeric indices via dot-string `'details.0.title'`);
 * non-numeric segments stay as strings; both join with `'.'`.
 */
function resolveControlPath(
  path: ReadonlyArray<PropertyKey>,
  fieldMap: Record<string, string>,
): string | null {
  if (path.length === 0) return null;

  // Try the joined path first; if no override, also try the top-level key
  // for backward compatibility with the previous `path[0]`-only behavior.
  const joined = path.map((segment) => segment.toString()).join('.');
  if (fieldMap[joined]) return fieldMap[joined];

  const top = path[0]?.toString();
  if (top && fieldMap[top]) {
    // Override the top-level segment but keep the remaining path.
    if (path.length === 1) return fieldMap[top];
    return [fieldMap[top], ...path.slice(1).map((s) => s.toString())].join('.');
  }

  return joined;
}

export function applyZodErrors(
  error: ZodError,
  form: FormGroup,
  fieldMap: Record<string, string> = {},
): boolean {
  let hasErrors = false;
  for (const issue of error.issues) {
    const controlPath = resolveControlPath(issue.path, fieldMap);
    if (!controlPath) continue;
    const control = form.get(controlPath);
    if (control) {
      control.setErrors({ zod: { message: issueToTranslationKey(issue) } });
      control.markAsTouched();
      hasErrors = true;
    }
  }
  return hasErrors;
}

/**
 * Maps a server-side 422 validation error bag onto matching form controls.
 * Reads `error.error.errors` (Laravel's `{ field: string[] }` shape) and sets
 * the first message per field as a `server` error on the corresponding control.
 */
export function applyServerErrors(error: unknown, form: FormGroup): void {
  const errBag = (error as { error?: { errors?: Record<string, string[]> } })
    ?.error?.errors;
  if (!errBag) return;
  for (const key of Object.keys(errBag)) {
    const control = form.get(key);
    if (control) {
      control.setErrors({ server: { message: errBag[key][0] } });
      control.markAsTouched();
    }
  }
}

export const validationTranslations = _([
  'validation.required',
  'validation.maxLength',
  'validation.invalidDate',
  'validation.invalidOption',
  'validation.invalid',
]);
